if (!window.ddPdpSizeGroupBound) {
  window.ddPdpSizeGroupBound = true;
  const pages = new Map();
  const dependentSections = ['.dd-section-26', '.dd-section-28', '.dd-section-18', '.dd-section-23'];
  let switching = false;
  let activeUrl = window.location.href;
  const cacheKey = (url) => new URL(url, window.location.origin).pathname;

  function rememberCurrent() {
    if (pages.has(cacheKey(activeUrl))) return;
    const root = document.querySelector('[data-dd-pdp]');
    const section = root.closest('.shopify-section');
    const modalId = root?.querySelector('dd-pdp-gallery')?.dataset.modalId;
    const modal = modalId && document.getElementById(modalId);
    let sectionHtml = section.outerHTML;
    if (modal && !section.contains(modal)) sectionHtml = sectionHtml.replace(/<\/section>\s*$/, `${modal.outerHTML}</section>`);
    const content = Array.from(document.querySelectorAll('#MainContent > .shopify-section')).map((node) => {
      if (node === section) return sectionHtml;
      return dependentSections.some((selector) => node.matches(selector)) ? node.outerHTML : node.cloneNode(false).outerHTML;
    }).join('');
    const head = Array.from(document.head.querySelectorAll('title, link[rel="canonical"], meta[name="description"], meta[property^="og:"], meta[name^="twitter:"]')).map((node) => node.outerHTML).join('');
    pages.set(cacheKey(activeUrl), { html: `<html><head>${head}</head><body><main id="MainContent">${content}</main></body></html>`, time: Date.now() });
  }

  function prepare(node, currentCart) {
    const copy = document.importNode(node, true);
    // A size switch replaces the purchasable selection. Do not restore quantities
    // or reward notices serialized from a previously visited size.
    copy.querySelectorAll('dd-pdp-product-form').forEach((form) => {
      form.querySelectorAll('[data-dd-pdp-purchase-row]').forEach((row) => {
        const selector = row.querySelector('[data-dd-pdp-quantity]');
        if (!selector) return;
        const quantity = row.dataset.rowType === 'current' && row.dataset.available === 'true'
          ? Math.max(1, Number(selector.dataset.min) || 1) : 0;
        selector.dataset.quantity = String(quantity);
        selector.dataset.quantityState = quantity > 0 ? 'active' : 'zero';
        const output = selector.querySelector('[data-dd-pdp-row-quantity]');
        const input = selector.querySelector('[data-dd-pdp-line-quantity]');
        if (output) output.textContent = String(quantity);
        if (input) { input.value = String(quantity); input.setAttribute('value', String(quantity)); }
      });
      const notice = form.querySelector('[data-dd-pdp-qualification]');
      if (notice) {
        notice.hidden = true;
        notice.classList.remove('is-visible', 'is-shipping', 'is-occluded', 'is-shipping-overlay');
        notice.removeAttribute('style');
      }
      const cartData = form.querySelector('[data-dd-pdp-commerce-cart]');
      if (cartData && Array.isArray(currentCart?.items)) cartData.textContent = JSON.stringify(currentCart);
    });
    // Cached markup has no event listeners; let each new element bind again.
    copy.querySelectorAll('[data-dd-bound]').forEach((element) => element.removeAttribute('data-dd-bound'));
    copy.querySelectorAll('dd-continuous-carousel').forEach((element) => {
      element.removeAttribute('data-initialized');
      element.querySelectorAll('[data-dd-loop-clone]').forEach((clone) => clone.remove());
    });
    copy.querySelectorAll('script').forEach((script) => {
      if (script.type !== 'application/ld+json' && script.type !== 'application/json') script.remove();
    });
    copy.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      // The current section may own this stylesheet. It disappears when that
      // section is replaced, so only a copy in <head> can be reused safely.
      if (!Array.from(document.head.querySelectorAll('link[rel="stylesheet"]')).some((existing) => existing.href === link.href)) document.head.append(link.cloneNode(true));
      link.remove();
    });
    return copy;
  }

  async function switchSize(url, addHistory = true) {
    const root = document.querySelector('[data-dd-pdp]');
    const form = root?.querySelector('dd-pdp-product-form');
    const group = form?.querySelector('.dd-size-group');
    if (!group || switching || form.isSubmitting || form.isCartPending) return false;
    rememberCurrent();
    switching = true;
    const hint = group.querySelector('.dd-size-group__hint');
    const controls = Array.from(group.querySelectorAll('button'));
    const loadingButton = controls.find((button) => cacheKey(button.dataset.ddSizeUrl) === cacheKey(url));
    group.dataset.loading = 'true';
    group.setAttribute('aria-busy', 'true');
    hint.textContent = group.dataset.ddSizeLoading;
    hint.classList.add('visually-hidden');
    loadingButton?.classList.add('is-loading');
    controls.forEach((button) => { button.disabled = true; });
    form.submitButtons?.forEach((button) => { button.disabled = true; });
    try {
      const target = new URL(url, window.location.origin);
      if (target.origin !== window.location.origin || !target.pathname.includes('/products/')) throw new Error('Invalid product URL');
      const cached = pages.get(cacheKey(target.href));
      let html;
      if (cached && Date.now() - cached.time < 120000) html = cached.html;
      else {
        const response = await fetch(target.href, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error('Size response failed');
        html = await response.text();
      }
      const page = new DOMParser().parseFromString(html, 'text/html');
      const incomingRoot = page.querySelector('[data-dd-pdp]');
      const incomingForm = incomingRoot?.querySelector('dd-pdp-product-form');
      const incomingSection = incomingRoot?.closest('.shopify-section');
      if (!incomingSection || !incomingForm?.querySelector('[data-dd-pdp-purchase-row]')) throw new Error('Missing product content');
      const selected = incomingForm.querySelector('.dd-size-group [aria-pressed="true"]');
      if (!selected || new URL(selected.dataset.ddSizeUrl, target).pathname !== target.pathname) throw new Error('Incorrect product content');
      const section = root.closest('.shopify-section');
      const replacement = prepare(incomingSection, form.commerceCart);
      window.DDSizeExperiment?.prepare(replacement);
      const oldBadge = form.querySelector('.stamped-product-reviews-badge');
      const newBadge = replacement.querySelector('.stamped-product-reviews-badge');
      if (oldBadge?.childElementCount && newBadge) newBadge.replaceChildren(...Array.from(oldBadge.childNodes).map((node) => node.cloneNode(true)));
      const changes = dependentSections.map((selector) => ({
        current: document.querySelector(`#MainContent > ${selector}`),
        incoming: page.querySelector(`#MainContent > ${selector}`),
      })).map((change) => ({ ...change, replacement: change.incoming ? prepare(change.incoming) : null }));
      const position = group.getBoundingClientRect().top;
      const modalId = root.querySelector('dd-pdp-gallery')?.dataset.modalId;
      const oldModal = modalId && document.getElementById(modalId);
      if (oldModal?.hasAttribute('open')) oldModal.hide();
      section.replaceWith(replacement);
      oldModal?.remove();
      changes.forEach((change) => {
        if (change.current) {
          if (change.replacement) change.current.replaceWith(change.replacement);
          else change.current.remove();
        } else if (change.replacement) {
          const following = Array.from(change.incoming.parentElement.children).slice(Array.from(change.incoming.parentElement.children).indexOf(change.incoming) + 1);
          const anchor = following.map((node) => {
            const exact = document.getElementById(node.id);
            if (exact) return exact;
            const type = Array.from(node.classList).find((name) => /^dd-section-\d+$/.test(name));
            if (!type) return null;
            const index = Array.from(page.querySelectorAll(`#MainContent > .${type}`)).indexOf(node);
            return document.querySelectorAll(`#MainContent > .${type}`)[index];
          }).find(Boolean);
          if (anchor) anchor.before(change.replacement);
          else document.getElementById('MainContent').append(change.replacement);
        }
      });
      document.title = page.title;
      ['link[rel="canonical"]', 'meta[name="description"]', 'meta[property^="og:"]', 'meta[name^="twitter:"]'].forEach((selector) => {
        document.head.querySelectorAll(selector).forEach((node) => node.remove());
        page.head.querySelectorAll(selector).forEach((node) => document.head.append(document.importNode(node, true)));
      });
      if (addHistory) {
        history.replaceState({ ...history.state, ddSizeUrl: activeUrl }, '', activeUrl);
        history.pushState({ ddSizeUrl: target.href }, '', target.href);
      }
      activeUrl = target.href;
      pages.set(cacheKey(activeUrl), { html, time: Date.now() });
      if (pages.size > 2) pages.delete(pages.keys().next().value);
      const nextGroup = replacement.querySelector('.dd-size-group');
      window.scrollBy(0, nextGroup.getBoundingClientRect().top - position);
      nextGroup.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
      window.DDSizeExperiment?.track('dd_ab_size_changed', { selected_size: selected.textContent.trim().split(/\s/)[0] });
      return true;
    } catch (error) {
      window.DDSizeExperiment?.track('dd_ab_error', { error_type: 'size_switch' });
      hint.textContent = group.dataset.ddSizeError;
      hint.classList.remove('visually-hidden');
      controls.forEach((button) => { button.disabled = false; });
      form.updateSummary?.();
      return false;
    } finally {
      switching = false;
      loadingButton?.classList.remove('is-loading');
      delete group.dataset.loading;
      group.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-dd-size-url]');
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute('aria-pressed') !== 'true') switchSize(button.dataset.ddSizeUrl);
  });
  window.addEventListener('popstate', async () => {
    if (window.location.href === activeUrl) return;
    if (!await switchSize(window.location.href, false)) window.location.reload();
  });
}
